import { useEffect, useMemo } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { newsRepo } from '@/data/repositories/news-repo';
import { useMarkNewsSeen } from '@/features/news/useUnseenNews';
import { dayBucket, fmtTime } from '@/ui/utils/format';
import type { NewsItem } from '@/domain/types';

export function NewsScreen() {
  const q = useQuery({ queryKey: ['news'], queryFn: newsRepo.latest });
  const markSeen = useMarkNewsSeen();

  useEffect(() => {
    if (q.data && q.data.length > 0) markSeen();
  }, [q.data, markSeen]);

  const groups = useMemo(() => {
    const m = new Map<string, NewsItem[]>();
    for (const it of q.data ?? []) {
      const key = dayBucket(it.createdat ?? 0);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(it);
    }
    return m;
  }, [q.data]);

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar
        title="News"
        sub="Updates, promos & system alerts"
        right={
          <button style={{ ...btnIcon, position: 'relative' }}>
            {sized(Icons.bell, 18)}
            {q.data && q.data.length > 0 && (
              <span style={{ position: 'absolute', top: 8, right: 9, width: 7, height: 7,
                borderRadius: '50%', background: T.accent, boxShadow: `0 0 6px ${T.accent}` }}/>
            )}
          </button>
        }
      />

      {q.isError && <ErrorBox message="Couldn't load news." onRetry={() => q.refetch()}/>}
      {q.isPending && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner/></div>
      )}
      {q.data && q.data.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: T.textDim, fontSize: 13 }}>
          No news yet.
        </div>
      )}

      {[...groups.entries()].map(([day, items]) => (
        <div key={day}>
          <div style={{ padding: '14px 24px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1.5, fontWeight: 600 }}>
              {day === 'today' ? 'Today' : day === 'yesterday' ? 'Yesterday' : day}
            </span>
            <div style={{ height: 1, flex: 1, background: T.line }}/>
          </div>
          <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it) => <NewsCard key={it.id} item={it}/>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function NewsCard({ item }: { item: NewsItem }) {
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{
          padding: '3px 8px', borderRadius: 6,
          background: 'rgba(198,255,61,0.10)', color: T.accent,
          fontSize: 10, fontWeight: 700, letterSpacing: 1,
        }}>UPDATE</span>
        <span style={{ flex: 1 }}/>
        <span style={{ fontSize: 11, color: T.textMute, fontFamily: T.fontMono }}>
          {fmtTime(item.createdat ?? 0)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: 'rgba(198,255,61,0.10)', color: T.accent,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{sized(Icons.spark, 18)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {item.title && (
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.35 }}>
              {item.title}
            </div>
          )}
          {item.msg && (
            <p style={{ fontSize: 12, color: T.textDim, lineHeight: 1.55,
              margin: item.title ? '6px 0 0' : 0 }}>{item.msg}</p>
          )}
        </div>
      </div>
    </div>
  );
}
